import { InputTypes } from "constants/enums";
import { IFormField } from "models/app";
import React, { useState } from "react";
import Modal from "components/UI/DialogModal";
import Form from "components/UI/Form";
import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import MuiButton from "components/UI/MuiButton";
import { IWorkshop, IWorkshopInput } from "models/workshop";
import { IVendor } from "models/vendor";
import { IVenue } from "models/venue";
import { IModality, ITrainer } from "models/trainer";
import { ITopic } from "models/topic";
import { ICompetency } from "models/competency";
import { useTranslation } from "react-i18next";
import { IProfLevel } from "models/profLevel";
import { useAlert } from "contexts/alertContext";
import DeleteIcon from "@mui/icons-material/Delete";
import { getUserData } from "helpers";
import { UserRoleName } from "types/auth";
import { ILanguage } from "models/language";
import { ICity } from "models/city";
import { ICountry } from "models/country";
import { ICompany } from "models/company";
import { attendancefrequencyRoles, capacityRoles, companiesRoles, competenciesRoles, editlinkRoles, evaluationValidFromRoles, evaluationValidToRoles, fromDateRoles, fromTimeRoles, languageRoles, linkRoles, modalityRoles, prerequisitRoles, requireInvitationRoles, roomRoles, toDateRoles, topicRoles, toTimeRoles, trainerRoles, vendorRoles, venusRoles, workshopEvaluationRoles } from "../FieldsRole";
import { ICompanyTopic } from "models/companyTopics";
import { IWorkshopEvaluation } from "models/workshopEvaluation";

interface Props {
  open: boolean;
  loading: boolean;
  workshop: IWorkshop;
  vendors: IVendor[];
  venues: IVenue[];
  trainers: ITrainer[];
  countries: ICountry[];
  cities: ICity[];
  topics: ITopic[] | ICompanyTopic[];
  modalities: IModality[];
  companies?: ICompany[];
  languages: ILanguage[];
  workshopEvaluations: IWorkshopEvaluation[];
  competencies: ICompetency[];
  profLevel: IProfLevel[];
  inputObj:IWorkshopInput;
  onClose: () => void;
  onSubmit: (data: any[]) => void;
  onChanges: (name: string, value: any) => void;
}

type Combination = {
  competencyId: string;
  levelOfProficiencyId: string;
};

const EditWorkshop: React.FC<Props> = ({
  open,
  loading,
  workshop,
  vendors,
  venues,
  companies,
  trainers,
  topics,
  countries,
  cities,
  competencies,
  modalities,
  profLevel,
  languages,
  workshopEvaluations,
  inputObj,
  onClose,
  onSubmit,
  onChanges,
}) => {

  const [competency, setCompetency] = useState<string>("");
  const [proficiency, setProficiency] = useState<string>("");
  const [combinations, setCombinations] = useState<Combination[]>(
    workshop.competencies.map((item) => {
      return {
        competencyId: item.competencyId,
        levelOfProficiencyId: item.levelOfProficiencyId,
      };
    })
  );
  const { setAlert } = useAlert();

  const userData = getUserData();
  const { roleName } = userData;


  const [selectedCountry, setSelectedCountry] = useState<string>(workshop.countryId);
  const [selectedCity, setSelectedCity] = useState<string>(workshop.cityId);
  const [selectedVenue, setSelectedVenue] = useState<string>(workshop.venueId);
  const [filteredCities, setFilteredCities] = useState<ICity[]>(cities);
  const [filteredVenues, setFilteredVenues] = useState<IVenue[]>(venues);


  const handleChangeCountry = (event: any) => {
    setSelectedCountry(event.target.value);
    const citiesForCountry = cities.filter((city) => city.countryId === event.target.value);
    setFilteredCities(citiesForCountry);
    setSelectedCity('');
    setSelectedVenue('');
    onChanges('venueId', '')
  }

  const handleChangeCity = (event: any) => {
    setSelectedCity(event.target.value);
    const venuesForCity = venues.filter((venue) => venue.cityId === event.target.value);
    setFilteredVenues(venuesForCity);
    setSelectedVenue('');
    onChanges('venueId', '')
  }

  const handleChangeVenue = (event: any) => {
    setSelectedVenue(event.target.value);
    onChanges('venueId', event.target.value)
  }

  let vendorOptions = vendors.map((vendor) => {
    return { value: vendor.id, label: vendor.name };
  });


  let trainerOptions = trainers.map((trainer) => {
    return {
      value: trainer.id,
      label: trainer.firstName + " " + trainer.lastName,
    };
  });

  let topicOptions = topics.map((topic) => {
    return { value: topic.id, label: topic.name };
  });

  let competencyOptions = competencies.map((competency) => {
    return { value: competency.id, label: competency.name };
  });

  let profLevelOptions = profLevel.map((prof) => {
    return { value: prof.id, label: prof.name };
  });

  let modalitiesOptions = modalities.map((modality) => {
    return { value: modality.id, label: modality.name };
  });
  let languageOptions: any = languages.map((language) => {
    return {
      value: language.id,
      label: language.name
    };
  });

  let workshopEvaluationOptions = workshopEvaluations.map((workshopEvaluation) => {
    return { value: workshopEvaluation.id, label: workshopEvaluation.title };
  });


  let statusOptions = [
    { id: "1", label: "Active" },
    { id: "2", label: "Cancelled" },
    { id: "3", label: "Postponed" },
  ].map((status) => {
    return { value: status.id, label: status.label };
  });

  const { t } = useTranslation();

  let basicFields: IFormField[] = [
    {
      name: "topicId",
      label: t("T_topics"),
      type: InputTypes.SELECT,
      placeholder: t("T_select_topics"),
      options: topicOptions,
      multiple: false,
      internalLabel: true,
      hidden: !topicRoles.includes(roleName as UserRoleName),
      required: true,
      defaultValue: topicOptions.find(
        (topic) => topic?.value?.toLowerCase() === workshop?.topicId?.toLowerCase()
      ),
    },
    {
      name: "vendorId",
      label: t("T_vendors"),
      type: InputTypes.SELECT,
      placeholder: t("T_select_vendor"),
      options: vendorOptions,
      multiple: false,
      internalLabel: true,
      hidden: !vendorRoles.includes(roleName as UserRoleName),
      required: true,
      defaultValue: vendorOptions.find(
        (vendor) => vendor.value === workshop.vendorId
      ),
    },
    {
      name: "trainerId",
      label: t("T_trainers"),
      type: InputTypes.SELECT,
      placeholder: t("T_select_trainer"),
      options: trainerOptions,
      multiple: false,
      internalLabel: true,
      hidden: !trainerRoles.includes(roleName as UserRoleName),
      required: false,
      defaultValue: trainerOptions.find(
        (trainer) => trainer.value === workshop.trainerId
      ),
    },

  ];

  let companyOptions = (companies || []).map((vendor) => {
    return { value: vendor.id, label: vendor.name };
  });

  let basicFields2: IFormField[] = [
    {
      name: "roomNumber",
      label: t("T_room_number"),
      type: InputTypes.TEXT,
      hidden: !roomRoles.includes(roleName as UserRoleName),
      placeholder: t("T_enter_room_number"),
      internalLabel: true,
      required: false,
      defaultValue: workshop.roomNumber,
    },
    {
      name: "capacity",
      label: t("T_capacity"),
      type: InputTypes.NUMBER,
      placeholder: t("T_enter_capacity"),
      internalLabel: true,
      hidden: !capacityRoles.includes(roleName as UserRoleName),
      required: true,
      defaultValue: workshop.capacity,
    },
    {
      name: "status",
      label: t("T_status"),
      type: InputTypes.SELECT,
      placeholder: t("T_select_status"),
      options: statusOptions,
      hidden: roleName === UserRoleName.VENDOR_ADMIN,
      // disabled: roleName === UserRoleName.COMPANY_ADMIN,
      internalLabel: true,
      required: true,
      defaultValue: statusOptions.find(
        (status) => status?.value === workshop.status?.toString()
      ),
    },
    {
      name: "modality",
      label: t("T_modalities"),
      type: InputTypes.SELECT,
      placeholder: t("T_select_modalities"),
      options: modalitiesOptions,
      multiple: false,
      // disabled: roleName === UserRoleName.COMPANY_ADMIN,
      hidden: !modalityRoles.includes(roleName as UserRoleName),
      internalLabel: true,
      required: true,
      defaultValue: modalitiesOptions.find(
        (modality) => modality?.value?.toString() === workshop?.modality?.toString()
      ),
    },
    {
            name: "coHost",
            label: t("T_cohost"),
            type: InputTypes.TEXT,
            options: modalitiesOptions,
            hidden: !modalityRoles.includes(roleName as UserRoleName) || inputObj.modality !== 2,
            internalLabel: true,
            required: true,
            defaultValue: workshop.coHost,
          },
    {
      name: "companies",
      label: t("T_companies"),
      placeholder: t("T_select_companies"),
      type: InputTypes.MULTI_SELECT,
      options: companyOptions,
      multiple: true,
      hidden: !companiesRoles.includes(roleName as UserRoleName),
      defaultValue: companyOptions.filter((lang: any) =>
        workshop?.companies?.find((l) => l === lang.value)
      ),
      internalLabel: true,
      required: true,
    },
    {
      name: "languages",
      label: t("T_languages"),
      type: InputTypes.MULTI_SELECT,
      placeholder: t("T_select_languages"),
      options: languageOptions,
      multiple: true,
      internalLabel: true,
      required: true,
      hidden: !languageRoles.includes(roleName as UserRoleName),
      defaultValue: languageOptions.filter((lang: any) =>
        workshop.languages.find((l) => l.id === lang.value)
      ),
    },
    {
      name: "link",
      label: t("T_link"),
      type: InputTypes.TEXT,
      placeholder: t("T_enter_link"),
      internalLabel: true,
      required: false,
      hidden: !editlinkRoles.includes(roleName as UserRoleName),
      defaultValue: workshop.link,
    },
    {
      name: "fromDate",
      label: t("T_from_date"),
      type: InputTypes.CALENDER,
      internalLabel: true,
      hidden: !fromDateRoles.includes(roleName as UserRoleName),
      required: false,
      defaultValue: new Date(workshop.fromDate),
    },
    {
      name: "toDate",
      label: t("T_to_date"),
      hidden: !toDateRoles.includes(roleName as UserRoleName),
      type: InputTypes.CALENDER,
      internalLabel: true,
      required: false,
      defaultValue: new Date(workshop.toDate),
    },
    {
      name: "fromTime",
      label: t("T_from_time"),
      type: InputTypes.TIME,
      internalLabel: true,
      hidden: !fromTimeRoles.includes(roleName as UserRoleName),
      required: false,
      defaultValue: workshop.fromTime,
    },
    {
      name: "toTime",
      label: t("T_to_time"),
      hidden: !toTimeRoles.includes(roleName as UserRoleName),
      type: InputTypes.TIME,
      internalLabel: true,
      required: false,
      defaultValue: workshop.toTime,
    },
    {
      name: "prerequisite",
      label: t("T_prerequisite"),
      type: InputTypes.TEXT,
      internalLabel: true,
      hidden: !prerequisitRoles.includes(roleName as UserRoleName),
      required: false,
      defaultValue: workshop.prerequisite
    },
    {
      name: "attendanceFrequency",
      label: t("T_attendance_frequency"),
      type: InputTypes.NUMBER,
      defaultValue: workshop.attendanceFrequency,
      hidden: !attendancefrequencyRoles.includes(roleName as UserRoleName),
      internalLabel: true,
      required: false,
    },
    {
      name: "requires_invitation",
      label: t("T_requires_invitation"),
      type: InputTypes.CHECKBOX,
      internalLabel: true,
      hidden: !requireInvitationRoles.includes(roleName as UserRoleName),
      required: false,
      defaultValue: workshop?.requiresInvitation || false
    },
    {
      name: "workshopEvaluationId",
      label: t("T_evaluation"),
      type: InputTypes.SELECT,
      placeholder: t("T_select_evaluation"),
      options: workshopEvaluationOptions,
      multiple: false,
      hidden: !workshopEvaluationRoles.includes(roleName as UserRoleName),
      internalLabel: true,
      required: true,
      defaultValue: workshopEvaluationOptions.find(
        (modality) => modality?.value?.toString() === workshop?.workshopEvaluationId?.toString()
      ),
    },
    {
      name: "evaluationValidFrom",
      label: t("T_evaluation_from_date"),
      type: InputTypes.CALENDER,
      hidden: !evaluationValidFromRoles.includes(roleName as UserRoleName),
      internalLabel: true,
      defaultValue: workshop.evaluationValidFrom?.toString(),
      required: true,
    },
    {
      name: "evaluationValidTo",
      label: t("T_evaluation_to_date"),
      defaultValue: workshop.evaluationValidTo?.toString(),
      type: InputTypes.CALENDER,
      hidden: !evaluationValidToRoles.includes(roleName as UserRoleName),
      internalLabel: true,
      required: true,
    },
    {
       name: "islocked",
      label: t("T_is_locked"),
      type: InputTypes.CHECKBOX,
      //hidden: !evaluationValidToRoles.includes(roleName as UserRoleName),
      internalLabel: true,
      defaultValue:workshop.isLocked      
    }
  ];

  const handleAddCombination = () => {
    if (competency && proficiency) {
      const newCombination = {
        competencyId: competency,
        levelOfProficiencyId: proficiency,
      };
      const exists = combinations.some(
        (comb) =>
          comb.competencyId === newCombination.competencyId &&
          comb.levelOfProficiencyId === newCombination.levelOfProficiencyId
      );
      if (!exists) {
        setCombinations([...combinations, newCombination]);
        setCompetency("");
        setProficiency("");
      } else {
        setAlert({
          open: true,
          message: "Already Exists",
          type: "info",
        });
      }
    }
  };

  const handleDeleteCombination = (index: number) => {
    const newCombinations = combinations.filter((_, i) => i !== index);
    setCombinations(newCombinations);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("T_edit_workshop")}
      maxWidth="lg"
      children={
        <Stack spacing={2} p={2}>
          <Form fields={basicFields} onChange={onChanges} />
          {venusRoles.includes(roleName as UserRoleName) && <div style={{ display: 'flex', justifyContent: 'center', marginBlock: 0, paddingBlock: 0, paddingRight: 10, gap: 20 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('T_country')}</InputLabel>
              <Select
                value={selectedCountry}
                onChange={handleChangeCountry}
                label={t('T_country')}
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal" disabled={!selectedCountry}>
              <InputLabel>{t('T_city')}</InputLabel>
              <Select
                value={selectedCity}
                onChange={handleChangeCity}
                label={t('T_city')}
              >
                {filteredCities.map((city: any) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>}
          {venusRoles.includes(roleName as UserRoleName) && <FormControl fullWidth margin="normal" disabled={!selectedCity} style={{ paddingRight: 10 }}>
            <InputLabel>{t('T_venus')}</InputLabel>
            <Select
              value={selectedVenue}
              onChange={handleChangeVenue}
              label={t('T_venus')}
            >
              {filteredVenues.map((venue: any) => (
                <MenuItem key={venue.id} value={venue.id}>
                  {venue.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>}

          <Form fields={basicFields2} onChange={onChanges} />
          {competenciesRoles.includes(roleName as UserRoleName) && <div style={{ marginTop: "20px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ width: "300px" }}>
                  <Autocomplete
                    disabled={roleName === UserRoleName.COMPANY_ADMIN || roleName === UserRoleName.CORPORATE_ADMIN}
                    value={
                      competencyOptions.find(
                        (comp) => comp.value === competency
                      ) || null
                    }
                    onChange={(event, newValue) =>
                      setCompetency(newValue?.value || "")
                    }
                    options={competencyOptions}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("T_select_competencies")}
                        variant="outlined"
                      />
                    )}
                  />
                </div>

                <Select
                  disabled={roleName === UserRoleName.COMPANY_ADMIN || roleName === UserRoleName.CORPORATE_ADMIN}
                  value={proficiency}
                  onChange={(e) => setProficiency(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {t("T_select_proficiency_level")}
                  </MenuItem>
                  {profLevelOptions.map((prof) => (
                    <MenuItem key={prof.value} value={prof.value}>
                      {prof.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              <Button
                variant="contained"
                sx={{ borderRadius: 0 }}
                onClick={handleAddCombination}
                disabled={
                  !competency ||
                  !proficiency ||
                  roleName === UserRoleName.COMPANY_ADMIN || roleName === UserRoleName.CORPORATE_ADMIN
                }
              >
                {t("T_add")}
              </Button>
            </div>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("T_competency_name")}</TableCell>
                  <TableCell>{t("T_proficiency_level")}</TableCell>
                  <TableCell>{t("T_action")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {combinations.map((comb, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {
                        competencies.find(
                          (comp) => comp.id === comb.competencyId
                        )?.name
                      }
                    </TableCell>
                    <TableCell>
                      {
                        profLevelOptions.find(
                          (prof) => prof.value === comb.levelOfProficiencyId
                        )?.label
                      }
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleDeleteCombination(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!!!combinations.length && (
              <div
                style={{
                  textAlign: "center",
                  margin: "10px auto",
                  color: "gray",
                }}
              >
                {t("T_no_data_selected")}
              </div>
            )}
          </div>}

        </Stack>
      }
      actions={
        <Grid
          container
          alignItems={"center"}
          justifyContent={"space-between"}
          px={1}
        >
          <MuiButton
            variant="contained"
            sx={{ bgcolor: "warning.main", color: "white" }}
            size="large"
            onClick={onClose}
          >
            {t("T_cancel")}
          </MuiButton>
          <MuiButton
            variant="contained"
            sx={{ bgcolor: "primary.main", color: "white" }}
            disabled={loading}
            size="large"
            onClick={() => onSubmit(combinations)}
          >
            {t("T_save")}
            <CircularProgress
              size={10}
              sx={{ mx: 2, display: loading ? "" : "none" }}
            />
          </MuiButton>
        </Grid>
      }
    />
  );
};

export default EditWorkshop;
